import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { toAnchored, toWorld, type BoardText, type Point } from "@board/shared";
import { useBoard } from "./store";
import type { InkRegion } from "./InkLayer";
import type { Transform } from "./useCanvasTransform";

const DRAG_THRESHOLD = 5;

/**
 * A label being typed. `item` holds the position in *world* coordinates while
 * it is a draft, because that is where the caret is on screen; `anchor` is the
 * column it will be stored against, or null for a label on the open board.
 */
type Draft = { item: BoardText; anchor: Point | null; fresh: boolean };

export function TextLayer({ transform, screenToWorld, regions, focusedClientId }: {
  transform: Transform;
  screenToWorld: (x: number, y: number) => { x: number; y: number };
  regions: InkRegion[];
  focusedClientId: string | null;
}) {
  const tool = useBoard((state) => state.tool);
  const setTool = useBoard((state) => state.setTool);
  const textItems = useBoard((state) => state.textItems);
  const selected = useBoard((state) => state.selectedObject);
  const selectObject = useBoard((state) => state.selectObject);
  const create = useBoard((state) => state.placeBoardText);
  const update = useBoard((state) => state.changeBoardText);
  const remove = useBoard((state) => state.removeBoardText);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [problem, setProblem] = useState("");
  const press = useRef<{ pointer: number; id: string; x: number; y: number;
    canvasX: number; canvasY: number; moved: boolean } | null>(null);
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);

  const displayed = useMemo(() => textItems
    .filter((item) => !focusedClientId || item.client_id === focusedClientId)
    .map((item) => {
      const anchor = regions.find((candidate) => candidate.clientId === item.client_id) ?? null;
      const at = toWorld(anchor, { x: item.canvas_x, y: item.canvas_y });
      return { item, x: at.x, y: at.y, anchor };
    }), [focusedClientId, regions, textItems]);

  function beginAt(clientX: number, clientY: number) {
    const point = screenToWorld(clientX, clientY);
    const region = regions.find((candidate) => point.x >= candidate.x
      && point.x <= candidate.x + candidate.width
      && point.y >= candidate.y && point.y <= candidate.y + candidate.height);
    const now = new Date().toISOString();
    setDraft({ item: { id: "draft", client_id: region?.clientId ?? null, text: "",
      canvas_x: point.x, canvas_y: point.y, color: "#23262a", font_size: 22,
      created_by: "", created_at: now, updated_at: now },
      anchor: region ?? null, fresh: true });
    setProblem("");
  }

  async function save(value: string) {
    if (!draft) return;
    const text = value.trim();
    if (!text) { setDraft(null); if (draft.fresh) setTool("select"); return; }
    try {
      if (draft.fresh) {
        // The draft carried a world position; stored, it is relative to its
        // column, so the label travels with the column from here on.
        const at = toAnchored(draft.anchor, { x: draft.item.canvas_x, y: draft.item.canvas_y });
        await create({ client_id: draft.item.client_id, text,
          canvas_x: at.x, canvas_y: at.y,
          color: draft.item.color, font_size: draft.item.font_size });
        setTool("select");
      } else await update(draft.item.id, { text }, "Edit text");
      setDraft(null); setProblem("");
    } catch { setProblem("Couldn’t save that text. Try again."); }
  }

  function pointerDown(event: ReactPointerEvent<HTMLElement>, item: BoardText) {
    if (event.button !== 0 || tool !== "select") return;
    event.stopPropagation(); event.preventDefault();
    selectObject({ kind: "text", id: item.id });
    event.currentTarget.focus();
    press.current = { pointer: event.pointerId, id: item.id, x: event.clientX, y: event.clientY,
      canvasX: item.canvas_x, canvasY: item.canvas_y, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: ReactPointerEvent<HTMLElement>) {
    const active = press.current;
    if (!active || active.pointer !== event.pointerId) return;
    const dx = event.clientX - active.x;
    const dy = event.clientY - active.y;
    if (!active.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    active.moved = true;
    setDrag({ id: active.id, x: dx / transform.scale, y: dy / transform.scale });
  }

  function pointerUp(event: ReactPointerEvent<HTMLElement>) {
    const active = press.current;
    if (!active || active.pointer !== event.pointerId) return;
    press.current = null;
    const moved = drag;
    setDrag(null);
    if (!active.moved || !moved) return;
    void update(active.id, { canvas_x: active.canvasX + moved.x, canvas_y: active.canvasY + moved.y }, "Move text")
      .catch(() => setProblem("Couldn’t move that text. Try again."));
  }

  return <>
    <div className="boardtexts" style={{ transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})` }}>
      {displayed.map(({ item, x, y, anchor }) => {
        if (draft?.item.id === item.id) return null;
        const isSelected = selected?.kind === "text" && selected.id === item.id;
        const offset = drag?.id === item.id ? drag : null;
        return <article key={item.id}
          className={`boardtext${isSelected ? " boardtext--selected" : ""}${offset ? " boardtext--dragging" : ""}`}
          style={{ left: x + (offset?.x ?? 0), top: y + (offset?.y ?? 0), color: item.color, fontSize: item.font_size }}
          title="Drag to move; double-click to edit" role="group" aria-label={`${item.text} text label`}
          tabIndex={tool === "select" ? 0 : -1}
          onPointerDown={(event) => pointerDown(event, item)} onPointerMove={pointerMove} onPointerUp={pointerUp}
          onPointerCancel={() => { press.current = null; setDrag(null); }}
          onDoubleClick={(event) => { event.stopPropagation(); if (tool === "select")
            setDraft({ item: { ...item, canvas_x: x, canvas_y: y }, anchor, fresh: false }); }}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault();
              setDraft({ item: { ...item, canvas_x: x, canvas_y: y }, anchor, fresh: false }); }
            else if (event.key === "Delete" || event.key === "Backspace") {
              event.preventDefault(); void remove(item.id).catch(() => setProblem("Couldn’t remove that text."));
            }
          }}>
          {item.text}
          {isSelected ? <button className="objectdelete" aria-label={`Remove ${item.text}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); void remove(item.id); }}>×</button> : null}
        </article>;
      })}
      {draft ? <TextEditor key={draft.item.id} item={draft.item} onSave={save}
        onCancel={() => { setDraft(null); setProblem(""); if (draft.fresh) setTool("select"); }} /> : null}
    </div>
    {tool === "text" && !draft ? <button className="textcapture" aria-label="Place text"
      onPointerDown={(event) => { if (event.button !== 0) return;
        event.preventDefault(); event.stopPropagation(); beginAt(event.clientX, event.clientY); }} /> : null}
    {problem ? <p className="toolproblem" role="alert">{problem}</p> : null}
  </>;
}

function TextEditor({ item, onSave, onCancel }: { item: BoardText;
  onSave: (value: string) => Promise<void>; onCancel: () => void }) {
  const [value, setValue] = useState(item.text);
  const saving = useRef(false);
  const commit = async () => { if (saving.current) return; saving.current = true;
    await onSave(value); saving.current = false; };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape") { event.preventDefault(); onCancel(); }
    else if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void commit(); }
  };
  return <textarea className="boardtext boardtext--editing"
    style={{ left: item.canvas_x, top: item.canvas_y, color: item.color, fontSize: item.font_size }}
    value={value} autoFocus rows={2} aria-label="Board text"
    onChange={(event) => setValue(event.target.value)} onKeyDown={onKeyDown} onBlur={() => void commit()} />;
}
